import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  styled,
  Chip,
  Skeleton,
  Stack,
  Button,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarToday as CalendarIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { format as formatDate } from "date-fns";
import { useTranslation } from "react-i18next";

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  border: "none",
  borderRadius: theme.spacing(2),
  transition: "all 0.3s ease",
  backgroundColor: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    "& .magazine-title": {
      color: theme.palette.primary.main,
    },
    "& .magazine-cover": {
      transform: "scale(1.05)",
    },
  },
}));

const MagazineCover = styled("img")({
  width: "100%",
  aspectRatio: "3/4",
  objectFit: "cover",
  transition: "transform 0.5s ease",
  "&.magazine-cover": {
    transform: "scale(1)",
  },
});

const CardContentStyled = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2),
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const MagazineTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.1rem",
  lineHeight: 1.4,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  height: "2.8em",
  color: theme.palette.text.primary,
  transition: "color 0.2s ease",
  textDecoration: "none",
  marginBottom: theme.spacing(0.5),
}));

const MagazineSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  display: "-webkit-box",
  WebkitLineClamp: 1,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
}));

const PriceTag = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "auto",
  width: "100%",
  gap: theme.spacing(1),
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(1.5),
  },
}));

const PriceInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  gap: theme.spacing(1),
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    justifyContent: "center",
  },
}));

const OriginalPrice = styled(Typography)(({ theme }) => ({
  textDecoration: "line-through",
  color: theme.palette.text.disabled,
  fontSize: "0.875rem",
}));

const CurrentPrice = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.25rem",
  color: theme.palette.primary.main,
}));

const PurchaseButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  fontWeight: 600,
  minWidth: "120px",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: theme.spacing(1.2, 2),
  },
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const InfoChip = styled(Chip)(({ theme }) => ({
  height: 24,
  fontSize: "0.75rem",
  backgroundColor: theme.palette.grey[100],
  "& .MuiChip-icon": {
    fontSize: "1rem",
  },
}));

const CategoryBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRtl",
})(({ theme, isRtl }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: isRtl ? 'auto' : theme.spacing(2),
  left: isRtl ? theme.spacing(2) : 'auto',
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  color: theme.palette.primary.main,
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.shape.borderRadius,
  fontWeight: 600,
  fontSize: "0.875rem",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  backdropFilter: "blur(8px)",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  zIndex: 1,
}));

const DiscountBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRtl",
})(({ theme, isRtl }) => ({
  position: "absolute",
  top: theme.spacing(2),
  left: isRtl ? 'auto' : theme.spacing(2),
  right: isRtl ? theme.spacing(2) : 'auto',
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.shape.borderRadius,
  fontWeight: 600,
  fontSize: "0.875rem",
  boxShadow: "0 2px 8px rgba(0,0,0,0.16)",
  zIndex: 1,
}));

const PublishInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
  "& .MuiSvgIcon-root": {
    fontSize: "1.1rem",
  },
}));

const MagazineCard = ({ magazine, onRead, loading }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';

  const handlePurchase = (e) => {
    e.preventDefault();
    navigate("/checkout", {
      state: {
        type: "MAGAZINE",
        items: {
          id: magazine.id,
          title: magazine.title,
          price: magazine.price,
          image: magazine.cover_image,
          subtitle: magazine.subtitle,
          publisher: magazine.publisher,
          publication_date: magazine.publication_date,
          category_display: magazine.category_display,
        },
      },
    });
  };

  if (loading) {
    return (
      <StyledCard>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={0}
          sx={{ paddingTop: "133.33%" }}
        />
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Skeleton variant="text" width="80%" height={24} />
          <Skeleton variant="text" width="60%" height={20} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <Skeleton width={80} height={32} />
            <Skeleton width={100} height={40} />
          </Box>
        </Stack>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      {magazine.discount_percentage > 0 && (
        <DiscountBadge isRtl={isRtl}>-{magazine.discount_percentage}% {t('magazineCard.off')}</DiscountBadge>
      )}

      <Box sx={{ position: "relative", overflow: "hidden" }}>
          <MagazineCover
            src={magazine.cover_image}
            alt={magazine.title}
            className="magazine-cover"
          />

        <CategoryBadge isRtl={isRtl}>
          <CategoryIcon sx={{ fontSize: "1rem" }} />
          {magazine.category_display}
        </CategoryBadge>
      </Box>

      <CardContentStyled>
        <Box>
          <MagazineTitle className="magazine-title">
            {magazine.title}
          </MagazineTitle>
          <MagazineSubtitle>{magazine.subtitle}</MagazineSubtitle>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <InfoChip
            icon={<CalendarIcon />}
            label={formatDate(new Date(magazine.publication_date), "MMM yyyy")}
            size="small"
          />
          <InfoChip
            icon={<StoreIcon />}
            label={magazine.publisher}
            size="small"
          />
        </Stack>

        <PriceTag>
          <PriceInfo>
            {magazine.discount_percentage > 0 && (
              <OriginalPrice>${magazine.original_price}</OriginalPrice>
            )}
            <CurrentPrice>${magazine.price}</CurrentPrice>
          </PriceInfo>
          <PurchaseButton
            onClick={handlePurchase}
            variant="contained"
            size="medium"
            fullWidth={window.innerWidth <= 600}
            disabled={magazine.is_purchased}
          >
            {magazine.is_purchased ? t('magazineCard.purchased') : t('magazineCard.purchase')}
          </PurchaseButton>
        </PriceTag>
      </CardContentStyled>
    </StyledCard>
  );
};

MagazineCard.propTypes = {
  magazine: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    publisher: PropTypes.string.isRequired,
    publication_date: PropTypes.string.isRequired,
    category_display: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    original_price: PropTypes.string,
    discount_percentage: PropTypes.number,
    cover_image: PropTypes.string.isRequired,
    is_published: PropTypes.bool.isRequired,
    is_purchased: PropTypes.bool.isRequired,
  }),
  onRead: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default React.memo(MagazineCard);
